// Pure reconciliation of a workflow's node tree from a workflowEvents stream.
// Kept out of the component so it's unit-testable without Apollo and reusable
// as the ingestion slideout grows.

export type NodeStatus = "PENDING" | "RUNNING" | "SUCCESS" | "ERROR";

export type Classification = {
  state: string;
  documentType?: string | null;
  entityId?: string | null;
  entityName?: string | null;
  reportingDate?: string | null;
  confidence?: string | null;
  failureReason?: string | null;
};

export type TimelineNode = {
  id: string;
  parentId: string | null;
  status: NodeStatus;
  summary: string;
  classification?: Classification;
};

export type WorkflowNodesState = Record<string, TimelineNode>;

/** A node upsert (full node) or patch (partial update), the two shapes the
 *  workflowEvents subscription delivers. `reset` clears state on open. */
export type NodeEvent =
  | { kind: "reset" }
  | {
      kind: "upsert";
      id: string;
      parentId: string | null;
      status: NodeStatus;
      summary: string;
      classification?: Classification;
    }
  | {
      kind: "patch";
      id: string;
      status?: NodeStatus | null;
      summary?: string | null;
      classification?: Classification;
    };

export const initialWorkflowNodes: WorkflowNodesState = {};

/** Pull a DocumentClassificationCard out of a node payload, if present. */
export function classificationFromPayload(payload: unknown): Classification | undefined {
  if (
    payload &&
    typeof payload === "object" &&
    (payload as { __typename?: string }).__typename === "DocumentClassificationCard"
  ) {
    return payload as Classification;
  }
  return undefined;
}

/**
 * Fold a NodeEvent into the node map.
 * - upsert: insert/replace the full node (keyed by id).
 * - patch: merge status/summary/classification onto an existing node; a patch
 *   for an unknown id is ignored (the upsert hasn't arrived yet).
 * - reset: clear the tree (new ingestion opened).
 */
export function workflowNodesReducer(
  state: WorkflowNodesState,
  event: NodeEvent,
): WorkflowNodesState {
  switch (event.kind) {
    case "reset":
      return initialWorkflowNodes;
    case "upsert":
      return {
        ...state,
        [event.id]: {
          id: event.id,
          parentId: event.parentId,
          status: event.status,
          summary: event.summary,
          classification: event.classification,
        },
      };
    case "patch": {
      const existing = state[event.id];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        [event.id]: {
          ...existing,
          status: event.status ?? existing.status,
          summary: event.summary ?? existing.summary,
          // A completion patch carries the proposed/failed classification.
          classification: event.classification ?? existing.classification,
        },
      };
    }
    default:
      return state;
  }
}

/** Adapt a raw `workflowEvents` subscription frame into a NodeEvent, or null
 *  if it carries no recognizable event. */
export function nodeEventFromStreamed(streamed: unknown): NodeEvent | null {
  const event = (streamed as { event?: { __typename?: string } } | undefined)?.event;
  if (!event) {
    return null;
  }
  if (event.__typename === "TimelineNodeUpserted") {
    const n = (event as { node: Record<string, unknown> }).node;
    return {
      kind: "upsert",
      id: n.id as string,
      parentId: (n.parentId as string | null) ?? null,
      status: n.status as NodeStatus,
      summary: n.summary as string,
      classification: classificationFromPayload(n.payload),
    };
  }
  if (event.__typename === "TimelineNodePatched") {
    const p = event as Record<string, unknown>;
    return {
      kind: "patch",
      id: p.id as string,
      status: (p.status as NodeStatus | null) ?? undefined,
      summary: (p.summary as string | null) ?? undefined,
      classification: classificationFromPayload(p.payload),
    };
  }
  return null;
}

/** Split the accumulated nodes into the root (parentless) and its descendants.
 *  Children = every non-root node, so the rows still render if the root event
 *  is missing/late (snapshot rehydrate, out-of-order delivery). */
export function selectRootAndChildren(state: WorkflowNodesState): {
  root: TimelineNode | undefined;
  children: TimelineNode[];
} {
  const all = Object.values(state);
  const root = all.find((n) => n.parentId == null);
  const children = all.filter((n) => n.id !== root?.id);
  return { root, children };
}
